import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Package, Users, Settings, BarChart3, MapPin, Building } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useLocationStore } from '@/stores/locationStore';
import LocationSelector from './LocationSelector';
import UserDropdown from './UserDropdown';
import { apiService } from '@/services/api';
import { UserRole } from '@/types';

const Layout: React.FC = () => {
  const location = useLocation();
  const { user } = useAuthStore();
  const { selectedLocationId, setSelectedLocation } = useLocationStore();
  const [logoPath, setLogoPath] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);

  const applyLogo = (value?: string | null) => {
    if (value && value.trim() !== '') {
      setLogoPath(value);
      setLogoError(false);
      return;
    }
    setLogoPath(null);
    setLogoError(true);
  };

  const fetchLogo = async () => {
    try {
      const publicLogo = await apiService.get<{ success?: boolean; logo?: string; value?: string }>(
        '/system/public/company_logo'
      );
      applyLogo(publicLogo?.logo || publicLogo?.value);
    } catch (error: any) {
      try {
        const logoSetting = await apiService.get<{ value: string }>('/system/company_logo');
        applyLogo(logoSetting?.value);
      } catch (fallbackError: any) {
        if (fallbackError.response?.status !== 403 && fallbackError.response?.status !== 404) {
          console.error('Error fetching logo setting:', fallbackError);
        }
        setLogoError(true);
      }
    }
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: BarChart3 },
    // Inventar nur für Admins und höher (nicht für normale USER)
    ...(user?.role !== UserRole.USER ? [{ name: 'Inventar', href: '/items', icon: Package }] : []),
    ...(user?.role !== UserRole.USER ? [{ name: 'Standorte', href: '/locations', icon: MapPin }] : []),
    { name: 'Transaktionen', href: '/transactions', icon: BarChart3 },
    ...(user?.role !== UserRole.USER ? [{ name: 'Benutzer', href: '/users', icon: Users }] : []),
    // SAP Info nur für Admins und höher
    ...(user?.role !== UserRole.USER ? [{ name: 'SAP Info', href: '/sap-info', icon: Building }] : []),
    // Einstellungen nur für Superadmins
    ...(user?.role === UserRole.SUPERADMIN ? [{ name: 'Einstellungen', href: '/settings', icon: Settings }] : []),
  ];

  useEffect(() => {
    fetchLogo();
  }, []);

  // Reload logo when user changes
  useEffect(() => {
    if (user) {
      fetchLogo();
    }
  }, [user]);

  // Remove handleLogout as it's now handled in UserDropdown

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            {logoPath && !logoError ? (
              <img
                src={logoPath}
                alt="Logo"
                className="h-8 w-auto object-contain"
                onError={(e) => {
                  console.error('Error loading logo:', e);
                  console.error('Logo path:', logoPath?.substring(0, 100) + '...');
                  setLogoError(true);
                }}
                onLoad={() => {
                  console.log('Logo loaded successfully');
                  setLogoError(false);
                }}
              />
            ) : (
              <h1 className="text-xl font-bold">Inventory Manager</h1>
            )}
            <nav className="hidden md:flex space-x-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
          
          <div className="flex items-center space-x-3">
            <LocationSelector
              selectedLocationId={selectedLocationId}
              onLocationChange={setSelectedLocation}
            />
            <UserDropdown />
          </div>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-8">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
